import { useState, useEffect, useMemo, useRef } from 'react';
import { 
  collection, 
  query, 
  onSnapshot,
  QueryConstraint,
  DocumentData,
  FirestoreError
} from 'firebase/firestore';
import { firestore } from '../config';

interface UseCollectionOptions {
  idField?: string;
}

export function useCollection<T = DocumentData>(
  path: string,
  constraints: QueryConstraint[] = [],
  options: UseCollectionOptions = {}
) {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<FirestoreError | null>(null);

  // Use ref to store constraints to avoid dependency issues
  const constraintsRef = useRef(constraints);

  // Update ref if constraints change
  useEffect(() => {
    constraintsRef.current = constraints;
  }, [constraints]);

  // Memoize the query to maintain referential stability
  const queryRef = useMemo(() => 
    query(collection(firestore, path), ...constraintsRef.current),
    [path] // path is stable, constraintsRef is always current
  );

  useEffect(() => {
    const unsubscribe = onSnapshot(
      queryRef,
      (snapshot) => {
        const docs = snapshot.docs.map(doc => ({
          ...(options.idField ? { [options.idField]: doc.id } : {}),
          ...doc.data()
        })) as T[];
        
        setData(docs);
        setLoading(false);
        setError(null);
      },
      (error) => {
        console.error('Collection error:', error);
        setError(error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [queryRef, options.idField]);

  return { data, loading, error };
}